/**
 * Cronbach's alpha over respondent × item scores — interne consistentie
 * van de items (vraag-rijen) van één MSI-effect.
 * Pure function — no Firebase imports.
 */

/**
 * @typedef {object} CronbachResult
 * @property {number|null} alpha - Cronbach's alpha, of null wanneer niet berekenbaar (k<2, n<2 of totaalvariantie 0)
 * @property {number} n - Aantal complete respondenten (listwise deletion)
 * @property {number} k - Aantal items
 */

/** @param {number[]} values */
const sampleVariance = (values) => {
    const n = values.length
    const mean = values.reduce((sum, v) => sum + v, 0) / n
    return values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / (n - 1)
}

/**
 * @param {Array<Array<number|null>>} rows - Eén array per respondent, één score per item
 * @returns {CronbachResult}
 */
const cronbachAlpha = (rows) => {
    const input = Array.isArray(rows) ? rows : []
    const k = input.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0)

    // Listwise deletion — alleen respondenten met een numerieke score op elk item
    const complete = input.filter((row) =>
        Array.isArray(row) && row.length === k && row.every((v) => Number.isFinite(v))
    )
    const n = complete.length

    if (k < 2 || n < 2) return { alpha: null, n, k }

    let itemVarianceSum = 0
    for (let item = 0; item < k; item += 1) {
        itemVarianceSum += sampleVariance(complete.map((row) => row[item]))
    }

    const totals = complete.map((row) => row.reduce((sum, v) => sum + v, 0))
    const totalVariance = sampleVariance(totals)
    if (totalVariance === 0) return { alpha: null, n, k }

    const alpha = (k / (k - 1)) * (1 - itemVarianceSum / totalVariance)
    return { alpha, n, k }
}

export default cronbachAlpha
